import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import SortableHeader from '../../components/common/SortableHeader';

const SmartOrder = () => {
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('ASC');

    const fetchSuggestions = useCallback(async () => {
        if (!user?.companyId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                companyId: user.companyId,
                page: page,
                limit: 20,
                sortBy: sortBy,
                sortOrder: sortOrder
            });
            const res = await fetch(`/api/purchase-orders/suggestions?${params.toString()}`);
            if (res.ok) {
                const data = await res.json();
                setSuggestions(data.suggestions);
                setPagination({ total: data.total, totalPages: data.totalPages });

                // Initialize quantities (but do NOT select by default as per user request)
                setQuantities(prevQtys => {
                    const newQtys = { ...prevQtys };
                    data.suggestions.forEach(s => {
                        if (newQtys[s.id] === undefined) {
                            newQtys[s.id] = s.suggestedQty;
                        }
                    });
                    return newQtys;
                });
            }
        } catch (error) {
            showToast("Erreur lors de la récupération des suggestions", "error");
        } finally {
            setLoading(false);
        }
    }, [user?.companyId, page, showToast, sortBy, sortOrder]);

    useEffect(() => {
        fetchSuggestions();
    }, [fetchSuggestions]);

    const toggleSelectAll = () => {
        if (selectedIds.length === suggestions.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(suggestions.map(s => s.id));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const updateQty = (id, delta) => {
        setQuantities(prev => ({
            ...prev,
            [id]: Math.max(0, (prev[id] || 0) + delta)
        }));
    };

    const summary = useMemo(() => {
        const suppliers = {};
        let total = 0;
        let count = 0;

        suggestions.filter(s => selectedIds.includes(s.id)).forEach(s => {
            const supName = s.supplier?.name || "Sans fournisseur";
            const supId = s.supplier?.id || "none";
            const price = parseFloat(s.supplier?.purchasePrice || s.price || 0);
            const qty = parseInt(quantities[s.id] || 0);
            const lineTotal = qty * price;

            if (!suppliers[supId]) {
                suppliers[supId] = { name: supName, total: 0, items: 0 };
            }
            suppliers[supId].total += lineTotal;
            suppliers[supId].items += 1;
            total += lineTotal;
            count += 1;
        });

        return { bySupplier: Object.values(suppliers), total, count };
    }, [suggestions, selectedIds, quantities]);

    const handleGenerateOrders = async () => {
        if (selectedIds.length === 0) {
            showToast("Sélectionnez au moins un article", "error");
            return;
        }

        const selectedItems = suggestions.filter(s => selectedIds.includes(s.id));
        const itemsWithoutSupplier = selectedItems.filter(s => !s.supplier?.id);

        if (itemsWithoutSupplier.length > 0) {
            showToast(`${itemsWithoutSupplier.length} article(s) n'ont pas de fournisseur associé et seront ignorés.`, "warning");

            if (itemsWithoutSupplier.length === selectedItems.length) {
                showToast("Aucun article sélectionné n'a de fournisseur. Impossible de générer des commandes.", "error");
                return;
            }
        }

        const hasSomePending = selectedItems.some(item => item.hasPendingOrder);

        const proceed = () => {
            const executeGeneration = async () => {
                try {
                    // Group items by supplier
                    const bySupplier = {};
                    selectedItems.forEach(s => {
                        const supId = s.supplier?.id;
                        if (!supId) return;
                        if (!bySupplier[supId]) bySupplier[supId] = [];
                        bySupplier[supId].push({
                            productId: s.id,
                            quantity: quantities[s.id],
                            unitPrice: parseFloat(s.supplier.purchasePrice || 0)
                        });
                    });

                    const supplierIds = Object.keys(bySupplier);
                    let successCount = 0;
                    let mergedCount = 0;

                    for (const supId of supplierIds) {
                        const items = bySupplier[supId];
                        const res = await fetch('/api/purchase-orders', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                supplierId: supId,
                                companyId: user.companyId,
                                userId: user.id,
                                reference: `AUTO-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
                                items,
                                notes: "Généré/Mis à jour automatiquement par Smart Order",
                                mergeIfDraft: true // ENABLE MERGE LOGIC
                            })
                        });

                        if (res.ok) {
                            if (res.status === 200) mergedCount++;
                            else successCount++;
                        }
                    }

                    if (successCount > 0 || mergedCount > 0) {
                        let msg = "";
                        if (successCount > 0) msg += `${successCount} nouveau(x) bon(s) créé(s). `;
                        if (mergedCount > 0) msg += `${mergedCount} brouillon(s) complété(s).`;

                        showToast(msg.trim() || "Opération réussie", "success");
                        fetchSuggestions(); // Refresh
                        setSelectedIds([]);
                    }
                } catch (error) {
                    showToast("Erreur lors de la génération", "error");
                }
            };

            executeGeneration();
        };

        if (hasSomePending) {
            showConfirm({
                title: "Commandes déjà en cours",
                message: "Certains articles sélectionnés sont déjà en cours de commande. Si une commande pour ces produits a déjà été envoyée, elle repassera automatiquement en 'Brouillon' pour que vous puissiez la compléter et la renvoyer. Souhaitez-vous continuer ?",
                onConfirm: proceed,
                type: 'warning'
            });
        } else {
            proceed();
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-background-light dark:bg-background-dark">
            {/* Header Section - Sticky with blur */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-sm pt-4 pb-6 mb-2 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex flex-wrap justify-between items-end gap-4 max-w-[1600px] mx-auto w-full px-6">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-3xl font-black leading-tight tracking-tight flex items-center gap-3 bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">
                            <span className="material-symbols-outlined text-primary text-3xl">auto_awesome</span>
                            Réapprovisionnement Intelligent
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                            Analyse de stock basée sur vos seuils de sécurité ({pagination.total} alertes au total).
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={fetchSuggestions}
                            className="size-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-background-light transition-all active:scale-95 shadow-sm"
                            title="Actualiser"
                        >
                            <span className="material-symbols-outlined text-slate-600 dark:text-slate-400">refresh</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="space-y-8 p-6 pt-4">
                <div className="max-w-[1600px] mx-auto space-y-8">
                    {/* Alertes de Stock Critique */}
                    <section>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="material-symbols-outlined text-rose-500">warning</span>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-wider text-sm">Alertes prioritaires</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {suggestions.filter(s => s.priority !== 'Moyen').slice(0, 3).map(s => (
                                <div key={s.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-3xl flex items-center gap-4 shadow-xl shadow-slate-200/50 dark:shadow-none">
                                    <div className={`size-14 rounded-xl flex-shrink-0 flex items-center justify-center ${s.priority === 'Rupture' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'
                                        }`}>
                                        <span className="material-symbols-outlined text-2xl">
                                            {s.priority === 'Rupture' ? 'block' : 'priority_high'}
                                        </span>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-bold text-sm text-slate-900 dark:text-white truncate">{s.name}</h3>
                                        <div className="flex items-center justify-between mt-1">
                                            <p className="text-xs text-slate-500">Stock : <span className="font-bold text-red-500">{s.quantity}</span></p>
                                            {s.autonomy != null && (
                                                <span className={`text-[10px] font-bold ${s.autonomy <= 3 ? 'text-rose-500' : 'text-amber-500'}`}>
                                                    ~{s.autonomy} jours
                                                </span>
                                            )}
                                        </div>
                                        <span className={`inline-block mt-2 px-2 py-0.5 text-[9px] font-black uppercase rounded-full ${s.priority === 'Rupture' ? 'bg-rose-500/10 text-rose-500' : 'bg-amber-500/10 text-amber-500'
                                            }`}>
                                            {s.priority}
                                        </span>
                                    </div>
                                </div>
                            ))}
                            {suggestions.filter(s => s.priority !== 'Moyen').length === 0 && (
                                <div className="md:col-span-3 p-8 bg-green-500/5 border border-green-500/20 rounded-2xl text-center">
                                    <p className="text-green-600 font-bold flex items-center justify-center gap-2">
                                        <span className="material-symbols-outlined">check_circle</span>
                                        Aucun stock critique identifié. Tout est sous contrôle !
                                    </p>
                                </div>
                            )}
                        </div>
                    </section>

                    <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
                        {/* Liste des articles */}
                        <div className="xl:col-span-3 space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2">
                                    <span className="material-symbols-outlined text-slate-400">list_alt</span>
                                    Suggestions de commande
                                </h2>
                                <button
                                    onClick={toggleSelectAll}
                                    className="px-3 py-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-[10px] font-black uppercase tracking-widest text-primary hover:bg-slate-50 transition-colors"
                                >
                                    {selectedIds.length === suggestions.length ? "Désélectionner tout" : "Sélectionner tout"}
                                </button>
                            </div>

                            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse table-fixed min-w-[900px]">
                                        <thead>
                                            <tr className="bg-background-light/50 dark:bg-slate-800/50 text-slate-500 text-[10px] uppercase tracking-widest font-black border-b border-slate-200 dark:border-slate-800">
                                                <th className="px-4 py-4 w-[50px] text-center"></th>
                                                <SortableHeader
                                                    label="Produit"
                                                    field="name"
                                                    currentSort={sortBy}
                                                    currentOrder={sortOrder}
                                                    onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                                    className="px-4 py-4 w-[30%]"
                                                />
                                                <SortableHeader
                                                    label="Fournisseur"
                                                    field="supplierName"
                                                    currentSort={sortBy}
                                                    currentOrder={sortOrder}
                                                    onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                                    className="px-4 py-4 w-[20%]"
                                                />
                                                <SortableHeader
                                                    label="Stock / Autonomie"
                                                    field="quantity"
                                                    currentSort={sortBy}
                                                    currentOrder={sortOrder}
                                                    onSort={(f, o) => { setSortBy(f); setSortOrder(o); }}
                                                    className="px-4 py-4 text-center justify-center w-[15%]"
                                                />
                                                <th className="px-4 py-4 text-center w-[15%]">Qté Comm.</th>
                                                <th className="px-4 py-4 text-right w-[15%]">Total Est.</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {loading ? (
                                                <tr>
                                                    <td colSpan="6" className="py-20 text-center">
                                                        <div className="animate-spin size-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                                                        <p className="text-slate-500 font-bold">Analyse en cours...</p>
                                                    </td>
                                                </tr>
                                            ) : suggestions.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="py-20 text-center text-slate-400">
                                                        <span className="material-symbols-outlined text-5xl mb-2 opacity-20">sentiment_satisfied</span>
                                                        <p className="font-bold">Aucune suggestion pour le moment.</p>
                                                    </td>
                                                </tr>
                                            ) : suggestions.map(s => (
                                                <tr key={s.id} className={`group hover:bg-background-light dark:hover:bg-slate-800/40 transition-colors ${selectedIds.includes(s.id) ? 'bg-primary/5' : ''}`}>
                                                    <td className="px-4 py-4">
                                                        <div className="flex justify-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedIds.includes(s.id)}
                                                                onChange={() => toggleSelect(s.id)}
                                                                className="size-5 rounded border-slate-300 dark:border-slate-700 text-primary focus:ring-primary/20 bg-transparent"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="min-w-0">
                                                            <p className="font-bold text-sm text-slate-900 dark:text-white truncate">{s.name}</p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <span className="text-[10px] text-slate-500 font-mono">{s.sku}</span>
                                                                <span className="text-[10px] text-slate-400">•</span>
                                                                <span className="text-[10px] text-slate-400 font-medium italic">{s.warehouseName}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        {s.supplier ? (
                                                            <div className="flex flex-col">
                                                                <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{s.supplier.name}</span>
                                                                <span className="text-[9px] text-slate-500 mt-0.5 uppercase tracking-wide">PU: {s.supplier.purchasePrice}€ • {s.supplier.leadTime}j</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-rose-500 font-bold italic">Aucun fournisseur</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <div className="flex flex-col items-center gap-1">
                                                            <span className={`text-sm font-black ${s.quantity <= s.minStock / 2 ? 'text-rose-500' : 'text-amber-500'}`}>
                                                                {s.quantity}
                                                            </span>
                                                            {s.autonomy != null && (
                                                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tight ${s.supplier?.leadTime > s.autonomy
                                                                        ? 'bg-rose-500 text-white animate-pulse'
                                                                        : s.autonomy <= 3
                                                                            ? 'bg-rose-100 text-rose-600'
                                                                            : 'bg-slate-100 text-slate-600'
                                                                    }`}>
                                                                    {s.autonomy} j restants
                                                                </span>
                                                            )}
                                                            <span className="text-[10px] text-slate-400 block uppercase font-bold">Min: {s.minStock}</span>
                                                        </div>
                                                        {s.hasPendingOrder && (
                                                            <div className="mt-2 p-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg text-center animate-pulse">
                                                                <p className="text-[9px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-tighter">En commande</p>
                                                                <p className="text-[10px] font-bold text-blue-500">+{s.pendingQty}</p>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                onClick={() => updateQty(s.id, -5)}
                                                                className="size-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors border border-slate-200 dark:border-slate-700"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">remove</span>
                                                            </button>
                                                            <input
                                                                type="number"
                                                                value={quantities[s.id] || 0}
                                                                onChange={(e) => setQuantities({ ...quantities, [s.id]: parseInt(e.target.value) || 0 })}
                                                                className="w-14 h-7 text-center bg-transparent border-b border-transparent focus:border-primary outline-none text-sm font-black text-slate-900 dark:text-white p-0"
                                                            />
                                                            <button
                                                                onClick={() => updateQty(s.id, 5)}
                                                                className="size-7 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:text-primary hover:bg-primary/10 transition-colors border border-slate-200 dark:border-slate-700"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">add</span>
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4 text-right">
                                                        <p className="text-sm font-black text-slate-900 dark:text-white font-mono">
                                                            {(parseInt(quantities[s.id] || 0) * parseFloat(s.supplier?.purchasePrice || s.price || 0)).toFixed(2)} €
                                                        </p>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination.totalPages > 1 && (
                                    <div className="px-6 py-4 bg-background-light/30 dark:bg-slate-800/30 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                                        <div className="flex flex-col">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Page {page} / {pagination.totalPages}</p>
                                            <p className="text-[9px] text-slate-400 mt-0.5">{pagination.total} articles au total</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => { setPage(p => Math.max(1, p - 1)); }}
                                                disabled={page === 1}
                                                className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-[11px] font-black uppercase tracking-wider disabled:opacity-30 hover:bg-background-light transition-colors shadow-sm"
                                            >
                                                Précédent
                                            </button>
                                            <button
                                                onClick={() => { setPage(p => Math.min(pagination.totalPages, p + 1)); }}
                                                disabled={page === pagination.totalPages}
                                                className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-[11px] font-black uppercase tracking-wider disabled:opacity-30 hover:bg-background-light transition-colors shadow-sm"
                                            >
                                                Suivant
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Récapitulatif Lateral */}
                        <div className="xl:col-span-1 space-y-6">
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-wider text-sm flex items-center gap-2">
                                <span className="material-symbols-outlined text-slate-400">payments</span>
                                Récapitulatif
                            </h2>
                            <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 shadow-xl shadow-slate-200/50 dark:shadow-none space-y-5 sticky top-6 backdrop-blur-sm">
                                <div className="space-y-5">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Par Fournisseur</p>
                                    <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                        {summary.bySupplier.map(sup => (
                                            <div key={sup.name} className="flex justify-between items-center p-3 rounded-xl bg-background-light dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-xs font-black truncate">{sup.name}</span>
                                                    <span className="text-[10px] text-slate-500 mt-0.5">{sup.items} article(s)</span>
                                                </div>
                                                <span className="font-black text-sm font-mono shrink-0">{sup.total.toFixed(2)} €</span>
                                            </div>
                                        ))}
                                        {summary.count === 0 && <p className="text-xs text-slate-400 italic text-center p-4">Aucune commande sélectionnée</p>}
                                    </div>
                                </div>

                                <div className="border-t border-slate-200 dark:border-slate-800 pt-6 space-y-3">
                                    <div className="flex justify-between text-xs font-bold text-slate-500 uppercase">
                                        <span>Total HT Estimé</span>
                                        <span className="text-slate-900 dark:text-white font-mono">{summary.total.toFixed(2)} €</span>
                                    </div>
                                    <div className="flex justify-between text-2xl font-black text-primary pt-2">
                                        <span>TOTAL</span>
                                        <span className="font-mono">{summary.total.toFixed(2)} €</span>
                                    </div>
                                </div>

                                <div className="pt-4 space-y-3">
                                    <button
                                        onClick={handleGenerateOrders}
                                        disabled={summary.count === 0}
                                        className="w-full py-4 bg-primary text-white rounded-xl font-black text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-3"
                                    >
                                        <span className="material-symbols-outlined">assignment_turned_in</span>
                                        Générer {summary.bySupplier.length} Commandes
                                    </button>
                                    <p className="text-[10px] text-center text-slate-400 font-medium">
                                        Les bons de commande seront créés en mode "Brouillon" dans le module Fournisseurs.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SmartOrder;
